import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Chip,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import PieChartRoundedIcon from "@mui/icons-material/PieChartRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";

import config from "./chatAssistantConfig";

/** Small glyph telling the user what a pin will come back as. */
function PinKindIcon({ pin }) {
  const sx = { fontSize: "0.95rem", color: config.COLORS.primary, flexShrink: 0 };
  if (pin.chart && pin.chart.type === "pie") return <PieChartRoundedIcon sx={sx} />;
  if (pin.chart && pin.chart.type === "line") return <ShowChartRoundedIcon sx={sx} />;
  if (pin.chart && pin.chart.type === "bar") return <BarChartRoundedIcon sx={sx} />;
  return <TableRowsRoundedIcon sx={sx} />;
}

function relativeDay(value) {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return then.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/**
 * The user's pinned questions, shown in place of the transcript.
 *
 * Clicking a pin replays it: the server re-runs the stored SQL against today's
 * data, so a pin is a saved *question*, not a saved answer - and because no
 * model call is involved it comes back immediately and costs nothing.
 */
export default function PinnedQueries({
  pins,
  loading,
  error,
  onRun,
  onDelete,
  busy,
  maxPins,
}) {
  return (
    <Box sx={{ px: 0.5 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.5,
          fontWeight: 700,
          color: "text.secondary",
          fontSize: "0.65rem",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        Pinned questions
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 1 }}>
          <CircularProgress size={13} thickness={5} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
            Loading your pins…
          </Typography>
        </Box>
      )}

      {!loading && error && (
        <Typography variant="body2" sx={{ fontSize: "0.78rem", color: "#8a2b2b", py: 1 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && !pins.length && (
        <Typography
          variant="body2"
          sx={{ fontSize: "0.78rem", color: "text.secondary", py: 1 }}
        >
          Nothing pinned yet. Ask a question, then use the pin button on the answer
          to keep it here — tables and charts both work.
        </Typography>
      )}

      <List dense disablePadding>
        {pins.map((pin) => (
          <ListItem
            key={pin.id}
            disableGutters
            disablePadding
            secondaryAction={
              <Tooltip title="Remove pin">
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => onDelete(pin)}
                  aria-label={`Remove pinned question: ${pin.label}`}
                  sx={{ p: 0.3 }}
                >
                  <DeleteOutlineRoundedIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            }
            sx={{ mb: 0.4 }}
          >
            <ListItemButton
              onClick={() => onRun(pin)}
              disabled={busy}
              sx={{
                borderRadius: 1,
                border: "1px solid #e3eaf2",
                bgcolor: "#fff",
                py: 0.5,
                pr: 4,
                alignItems: "flex-start",
                gap: 0.75,
                "&:hover": { bgcolor: `${config.COLORS.primary}0A` },
              }}
            >
              <Box sx={{ pt: 0.25 }}>
                <PinKindIcon pin={pin} />
              </Box>
              <ListItemText
                primary={pin.label}
                secondary={
                  pin.runCount > 0
                    ? `Opened ${pin.runCount} time${pin.runCount === 1 ? "" : "s"} · pinned ${relativeDay(pin.pinnedAt)}`
                    : `Pinned ${relativeDay(pin.pinnedAt)}`
                }
                primaryTypographyProps={{
                  fontSize: "0.76rem",
                  lineHeight: 1.35,
                  sx: {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  },
                }}
                secondaryTypographyProps={{ fontSize: "0.63rem" }}
                sx={{ my: 0 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {!loading && pins.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.4,
            mt: 0.75,
            color: "text.disabled",
            fontSize: "0.62rem",
          }}
        >
          <PushPinRoundedIcon sx={{ fontSize: "0.8rem" }} />
          Pins are saved to your account and re-run against the latest data.
          <Chip
            size="small"
            label={`${pins.length}/${maxPins}`}
            sx={{ height: 15, fontSize: "0.58rem", ml: "auto" }}
          />
        </Typography>
      )}
    </Box>
  );
}
